
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface CarouselImage {
  url: string;
  alt: string;
}

export const useHeroCarousel = (memorialId: string | undefined) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [featuredPhotos, setFeaturedPhotos] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchFeaturedPhotos = async () => {
      if (!memorialId) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      try {
        const { data: featuredData, error } = await supabase
          .from('featured_photos')
          .select('*')
          .eq('memorial_id', memorialId)
          .order('order_index', { ascending: true });

        if (error) {
          console.error('[HeroCarousel] Error fetching featured photos:', error);
          throw error;
        }

        if (featuredData && featuredData.length > 0) {
          const photoUrls = await Promise.all(featuredData.map(async (photo) => {
            let storagePath = photo.url;
            
            // Update to use featured-photos path
            if (!storagePath.includes(`featured-photos/${memorialId}/`)) {
              storagePath = `featured-photos/${memorialId}/${photo.url}`;
            }
            
            const { data: { publicUrl } } = supabase.storage
              .from('memorials')
              .getPublicUrl(storagePath);

            return publicUrl;
          }));

          setFeaturedPhotos(photoUrls);
        } else {
          setFeaturedPhotos([]);
        }
      } catch (error) {
        console.error('[HeroCarousel] Error processing featured photos:', error);
        setFeaturedPhotos([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedPhotos();
  }, [memorialId]);

  useEffect(() => {
    if (featuredPhotos.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % featuredPhotos.length);
      }, 3000);

      return () => clearInterval(timer);
    }
  }, [featuredPhotos.length]);

  return {
    currentSlide,
    featuredPhotos,
    isLoading
  };
};
